import { H2 } from '@/common/ui/h2'
import { Dialog, DialogContent } from '@/common/ui/dialog'
import { useRef, useState } from 'react'
import { useClickAway } from 'react-use'
import { isAxiosError } from 'axios'
import { toast } from 'sonner'
import useUserStore from '@/user/userStore'
import { Button } from '@/common/ui/button'
import { X } from 'lucide-react'
import ResetPassword from '@/auth/resetPassword'
import { ContactSupport } from './contactSupport'
import { ContactSupportModal } from './contactSupportModal'
import { MfaRegistrationModal } from '@/auth/features/bff-auth/mfa/components/MfaRegistrationModal'
import { bffAuthApi } from '@/auth/features/bff-auth/api/bffAuthApi'
import { clearTrustedDevice, getValidTrustedDevice } from '@/auth/features/bff-auth/trustedDevice/trustedDeviceStorage'
import { NAVIGATION_COPY } from '@/locales/en'
import { AccountInfoItem } from './components/AccountInfoItem'
import { useProfileDetailItems } from './model/useProfileDetailItems'
import { usePasswordResetFlow } from './model/usePasswordResetFlow'
import type { BffAuthErrorEnvelope } from '@/auth/features/bff-auth/types'

function getBffAuthErrorMessage(error: unknown): string {
    if (isAxiosError<BffAuthErrorEnvelope>(error) && error.response?.data?.message) {
        return error.response.data.message
    }
    return error instanceof Error ? error.message : String(error)
}

export default function ProfileSettingsView() {
    const { user } = useUserStore()
    const [isContactSupportOpen, setIsContactSupportOpen] = useState(false)
    const [isMfaModalOpen, setIsMfaModalOpen] = useState(false)

    const {
        isPasswordChangeOpen,
        isResettingPassword,
        sendingConfirmation,
        openPasswordChange,
        closePasswordChange,
        closePasswordReset,
        openResetPasswordForm,
        handleReset,
    } = usePasswordResetFlow(user)

    const handleForgetDevice = async () => {
        if (!user) return
        const trustedDevice = getValidTrustedDevice(user.emailId)
        if (!trustedDevice) return
        try {
            await bffAuthApi.deviceForget({ deviceKey: trustedDevice.deviceKey })
            clearTrustedDevice()
            toast.success(NAVIGATION_COPY.account.trustedDeviceFields.forgetSuccess)
        } catch (error: unknown) {
            toast.error(getBffAuthErrorMessage(error))
        }
    }

    const { items } = useProfileDetailItems({
        onEditPassword: openPasswordChange,
        onEditMfa: () => setIsMfaModalOpen(true),
        onForgetDevice: handleForgetDevice,
    })

    const passwordChangeAlertRef = useRef(null)
    useClickAway(passwordChangeAlertRef, () => {
        closePasswordChange()
    })

    const passwordResetModalRef = useRef(null)
    useClickAway(passwordResetModalRef, () => {
        closePasswordReset()
    })

    if (!user) return null

    return (
        <div>
            <H2 className="mt-5">Personal details</H2>
            <ul className="mt-2 mb-16">
                {items.map((item) => (
                    <AccountInfoItem
                        key={item.key}
                        title={item.label}
                        info={item.value}
                        editHandler={item.editHandler}
                        actionLabel={item.actionLabel}
                    />
                ))}
            </ul>

            {isPasswordChangeOpen && (
                <Dialog open={isPasswordChangeOpen}>
                    <div className="py-10 h-screen">
                        <DialogContent
                            ref={passwordChangeAlertRef}
                            onClose={closePasswordChange}
                            className="px-5 py-6 max-h-[90vh] w-[94vw] max-w-[680px] overflow-y-auto xs:p-20"
                        >
                            <H2 className="text-center">Change your password</H2>
                            <p className="text-center py-5">
                                In order to change your password, You must have access to your email. Do you wish to
                                continue?
                            </p>
                            <div className="flex justify-between">
                                <Button
                                    variant="secondary"
                                    onClick={openResetPasswordForm}
                                    loading={sendingConfirmation}
                                >
                                    Yes, I have access to my email
                                </Button>
                                <Button variant="ghost" className="hover:bg-transparent" onClick={closePasswordChange}>
                                    <div className="bg-accent rounded-full p-1">
                                        <X className="h-5 w-5" />
                                    </div>
                                    Cancel
                                </Button>
                            </div>
                        </DialogContent>
                    </div>
                </Dialog>
            )}

            {isResettingPassword && (
                <Dialog open={isResettingPassword}>
                    <div className="py-10 h-screen">
                        <DialogContent
                            className="px-5 py-12 max-h-[90vh] w-[94vw] max-w-[680px] overflow-y-auto xs:p-20"
                            onClose={closePasswordReset}
                        >
                            <ResetPassword onReset={handleReset} />
                        </DialogContent>
                    </div>
                </Dialog>
            )}
            {isMfaModalOpen && (
                <MfaRegistrationModal
                    email={user.emailId}
                    onClose={() => setIsMfaModalOpen(false)}
                    onEnabled={() => setIsMfaModalOpen(false)}
                />
            )}
            <ContactSupport showModal={() => setIsContactSupportOpen(true)} />
            {isContactSupportOpen && <ContactSupportModal closeModal={() => setIsContactSupportOpen(false)} />}
        </div>
    )
}
